/* CUSTOM — Auto-advancing carousel

   Shared by any section that wants a track that advances on its own below
   750px: the homepage reviews and the community hair types today. Above that
   breakpoint those sections are already grids showing everything at once, so
   there is nothing to advance and this stands down entirely.

   The element is attribute-driven — a track, an optional toggle, an interval —
   so a section opts in by marking up its own list. Nothing here knows what it
   is scrolling.

   Scrolling the real track rather than transforming it means the swipe, the
   snapping and the keyboard scroll are all the browser's, and the autoplay is
   only a timer calling scrollTo.

   The loop is seamless rather than a rewind: one cloned set is appended, so
   after the last review the track carries on forward into a copy of the first.
   Once that copy is reached the scroll position is moved back by exactly one
   set — the pixels either side of that move are identical, so nothing shows.
   Clones only exist while the carousel is actually running; above 750px they
   would turn a three-column grid into six. */

const MOBILE = '(max-width: 749px)';
// Long enough for a smooth scroll to settle before a manual scroll is inferred.
const SETTLE_MS = 800;

class MgCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-carousel-track]');
    this.toggle = this.querySelector('[data-carousel-toggle]');
    this.label = this.querySelector('[data-carousel-toggle-label]');
    this.realSlides = Array.from(this.track?.children || []);
    if (!this.track || this.realSlides.length < 2) return;

    this.interval = Number(this.dataset.interval) || 0;
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.mobile = window.matchMedia(MOBILE);

    // Paused by the visitor, as opposed to paused because the tab is hidden or
    // a pointer is resting on the track. Only this one survives those.
    this.userPaused = false;
    this.timer = null;
    this.settleTimer = null;
    this.cloned = false;

    // The slide we intend to be on, counted across the cloned set. Held rather
    // than measured every tick: a smooth scroll takes a few hundred ms, and
    // reading the position while it is still travelling rounds back to the
    // slide we just left, so the next tick re-targets the same slide and the
    // carousel never gets anywhere. Measurement is reconciled only once the
    // track is idle — see the scroll handler.
    this.pos = 0;
    this.autoScrolling = false;

    this.#bind();
    this.#sync();
  }

  disconnectedCallback() {
    this.#stop();
    clearTimeout(this.settleTimer);
    this.mobile?.removeEventListener('change', this.onBreakpoint);
    this.reducedMotion?.removeEventListener('change', this.onBreakpoint);
    document.removeEventListener('visibilitychange', this.onVisibility);
  }

  /** Ticker mode scrolls continuously rather than stepping slide to slide. The
   *  movement is a CSS animation on the track — one `translateX(-50%)` across
   *  the doubled set, which lands exactly on the clone of the first slide, so
   *  it repeats seamlessly. Kept in CSS rather than a timer because a linear
   *  transform is composited and a stepping timer is not: at ticker speeds the
   *  difference between the two is the whole point. */
  get isTicker() {
    return this.dataset.mode === 'ticker';
  }

  get canRun() {
    return this.mobile.matches && !this.reducedMotion.matches;
  }

  get canAutoplay() {
    return !this.isTicker && this.interval > 0 && this.canRun;
  }

  get count() {
    return this.realSlides.length;
  }

  #bind() {
    this.onBreakpoint = () => this.#sync();
    this.mobile.addEventListener('change', this.onBreakpoint);
    this.reducedMotion.addEventListener('change', this.onBreakpoint);

    // A hidden tab throttles timers to a crawl and never advances a smooth
    // scroll, so the carousel would come back mid-slide. Stop instead.
    this.onVisibility = () => (document.hidden ? this.#stop() : this.#maybeStart());
    document.addEventListener('visibilitychange', this.onVisibility);

    this.toggle?.addEventListener('click', (event) => {
      event.stopPropagation();
      this.#setPaused(!this.userPaused);
    });

    // Tapping the track pauses and resumes — but not when the tap landed on
    // something that does its own thing, or a review's product link would pause
    // the carousel on its way to the product page.
    this.track.addEventListener('click', (event) => {
      if (event.target.closest('a, button')) return;
      this.#setPaused(!this.userPaused);
    });

    // A pointer resting on the track, or focus inside it, holds position
    // without counting as a deliberate pause.
    this.addEventListener('pointerenter', () => this.#stop());
    this.addEventListener('pointerleave', () => this.#maybeStart());
    this.addEventListener('focusin', () => this.#stop());
    this.addEventListener('focusout', () => {
      if (!this.contains(document.activeElement)) this.#maybeStart();
    });

    // Only a scroll we did not cause counts as the visitor taking over: resync
    // to wherever they landed and give them a full interval on it.
    this.track.addEventListener(
      'scroll',
      () => {
        if (this.autoScrolling) return;
        clearTimeout(this.settleTimer);
        this.settleTimer = setTimeout(() => {
          this.pos = this.#measureIndex();
          this.#normalise();
        }, 120);
        if (!this.timer) return;
        this.#stop();
        this.#maybeStart();
      },
      { passive: true }
    );
  }

  /** One extra copy of every review, appended. Hidden from assistive tech and
   *  taken out of the tab order so the duplicate is purely visual, and stripped
   *  of the editor's block attributes so the theme editor still targets one
   *  element per block. */
  #addClones() {
    if (this.cloned) return;
    const fragment = document.createDocumentFragment();
    this.realSlides.forEach((slide) => {
      const clone = slide.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      clone.setAttribute('data-carousel-clone', '');
      clone.removeAttribute('id');
      clone.removeAttribute('data-shopify-editor-block');
      clone.querySelectorAll('a, button, [id]').forEach((node) => {
        node.removeAttribute('id');
        if (node.matches('a, button')) node.setAttribute('tabindex', '-1');
      });
      fragment.appendChild(clone);
    });
    this.track.appendChild(fragment);
    this.cloned = true;
  }

  #removeClones() {
    if (!this.cloned) return;
    this.track.querySelectorAll('[data-carousel-clone]').forEach((node) => node.remove());
    this.cloned = false;
    this.#suppress(() => {
      this.track.scrollLeft = 0;
    });
    this.pos = 0;
  }

  #slides() {
    return Array.from(this.track.children);
  }

  #sync() {
    if (this.isTicker) {
      const run = this.canRun;
      if (this.toggle) this.toggle.hidden = !run;
      if (!run) {
        this.classList.remove('is-running');
        this.#removeClones();
        return;
      }
      // The animation is only correct once the set is doubled — before that a
      // -50% translate would land halfway through the originals. The class is
      // what the CSS keys the animation off, so it goes on after cloning.
      this.#addClones();
      this.classList.add('is-running');
      return;
    }

    const on = this.canAutoplay;
    if (this.toggle) this.toggle.hidden = !on;
    if (!on) {
      this.#stop();
      this.#removeClones();
      return;
    }
    this.#addClones();
    this.pos = this.#measureIndex();
    this.#maybeStart();
  }

  #setPaused(paused) {
    this.userPaused = paused;
    if (this.toggle) {
      this.toggle.setAttribute('aria-pressed', String(paused));
      this.toggle.classList.toggle('is-paused', paused);
    }
    if (this.label) this.label.textContent = paused ? this.dataset.playLabel || 'Play' : this.dataset.pauseLabel || 'Pause';
    // Ticker mode has no timer to stop — the CSS animation is paused by class.
    this.classList.toggle('is-paused', paused);
    if (paused) this.#stop();
    else this.#maybeStart();
  }

  #maybeStart() {
    if (this.timer || this.userPaused || document.hidden || !this.canAutoplay) return;
    this.timer = setInterval(() => this.#advance(), this.interval);
  }

  #stop() {
    if (!this.timer) return;
    clearInterval(this.timer);
    this.timer = null;
  }

  /** Run a scroll change without the scroll handler mistaking it for the
   *  visitor taking over. */
  #suppress(fn) {
    this.autoScrolling = true;
    fn();
    clearTimeout(this.settleTimer);
    this.settleTimer = setTimeout(() => {
      this.autoScrolling = false;
    }, SETTLE_MS);
  }

  /** Distance from the track's scroll origin to a slide. Measured from rects
   *  plus the current scroll rather than offsetLeft, which is relative to
   *  whichever ancestor happens to be positioned. */
  #offsetOf(slide) {
    return (
      slide.getBoundingClientRect().left -
      this.track.getBoundingClientRect().left +
      this.track.scrollLeft
    );
  }

  /** Width of one full set of slides — the distance the loop rewinds by. */
  #setWidth() {
    const slides = this.#slides();
    if (slides.length <= this.count) return 0;
    return this.#offsetOf(slides[this.count]) - this.#offsetOf(slides[0]);
  }

  #measureIndex() {
    const left = this.track.scrollLeft;
    let best = 0;
    let bestGap = Infinity;
    this.#slides().forEach((slide, i) => {
      const gap = Math.abs(this.#offsetOf(slide) - left);
      if (gap < bestGap) {
        bestGap = gap;
        best = i;
      }
    });
    return best;
  }

  /** Once the track has run into the cloned set, step the scroll position back
   *  by exactly one set. The pixels are identical either side, so there is
   *  nothing to see — this is what makes the loop continuous rather than a
   *  rewind to the start. */
  #normalise() {
    if (!this.cloned) return;
    const width = this.#setWidth();
    if (!width) return;
    if (this.track.scrollLeft < width - 1) return;
    this.#suppress(() => {
      this.track.scrollLeft -= width;
    });
    this.pos -= this.count;
    if (this.pos < 0) this.pos = 0;
  }

  #advance() {
    // Self-correcting: if a breakpoint or reduced-motion change is ever missed,
    // the next tick stands the carousel down rather than scrolling a grid.
    if (!this.canAutoplay) {
      this.#stop();
      this.#removeClones();
      return;
    }

    const slides = this.#slides();
    this.pos = Math.min(this.pos + 1, slides.length - 1);
    const target = this.#offsetOf(slides[this.pos]);

    this.#suppress(() => {
      this.track.scrollTo({ left: target, behavior: 'smooth' });
    });

    // Reached the copy of the first review: let the scroll land, then step back
    // one set so the next tick carries on forward instead of rewinding.
    if (this.pos >= this.count) {
      clearTimeout(this.settleTimer);
      this.settleTimer = setTimeout(() => {
        this.#normalise();
        this.autoScrolling = false;
      }, SETTLE_MS);
    }
  }
}

if (!customElements.get('mg-carousel')) customElements.define('mg-carousel', MgCarousel);
